import { ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

const SHIFT_START_MIN = 7 * 60; //AM
const SHIFT_END_MIN = 19 * 60; // PM

const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [now, setNow] = useState(new Date());
  const [glow, setGlow] = useState({ x: 70, y: 25 });

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setGlow({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  let hours = now.getHours();
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const hh = String(hours).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");

  const date = now.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const shiftPercent = Math.min(
    100,
    Math.max(
      0,
      ((nowMin - SHIFT_START_MIN) / (SHIFT_END_MIN - SHIFT_START_MIN)) * 100
    )
  );

  return (
    <div
      className="min-h-screen w-full flex bg-[#FAF9F6] relative font-sans"
      style={
        {
          "--primary": "#2C4A6E",
          "--primary-foreground": "#FAF7F2",
          "--ring": "#2C4A6E",
        } as React.CSSProperties
      }
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>

      {/* ─── Left: identity + live register clock ─── */}
      <div
        onMouseMove={handleMouseMove}
        className="hidden lg:flex w-[52%] flex-col justify-between bg-[#16181C] text-white p-12 xl:p-16 relative overflow-hidden"
      >
        {/* interactive ambient glow — follows the cursor, subtle */}
        <div
          className="absolute w-[420px] h-[420px] rounded-full opacity-[0.14] blur-[100px] pointer-events-none"
          style={{
            background: "#6FA3D8",
            left: `${glow.x}%`,
            top: `${glow.y}%`,
            transform: "translate(-50%, -50%)",
            transition: "left 400ms ease-out, top 400ms ease-out",
          }}
        />

        {/* Header: monogram + trust name */}
        <div className="relative z-10 flex items-center gap-3">
          <div
            className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm shrink-0"
            style={{ background: "#2C4A6E", fontFamily: "'IBM Plex Mono', monospace" }}
          >
            S
          </div>
          <div>
            <p
              className="text-[13px] font-medium text-white leading-none"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Sadguru Seva Sangh Trust
            </p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#8B9099] font-medium mt-1">
              Daily Wage Register
            </p>
          </div>
        </div>

        {/* Main: headline + signature clock */}
        <div className="relative z-10 max-w-md space-y-10">
          <div className="space-y-4">
            <h1
              className="text-[2.5rem] xl:text-[2.85rem] font-semibold leading-[1.1] tracking-tight text-white"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Time in. Wages tracked.
            </h1>
            <p className="text-[14.5px] text-[#9CA3AF] leading-relaxed max-w-sm">
              Sign in to record attendance and calculate pay across every
              site your trust runs.
            </p>
          </div>

          {/* Signature: live register clock */}
          <div className="rounded-xl border border-white/10 bg-[#1E2126] px-6 py-5 max-w-xs">
            <div className="flex items-center justify-between mb-3">
              <span
                className="text-[10px] uppercase tracking-[0.16em] text-[#8B9099] font-medium"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                Local time
              </span>
              <span className="flex items-center gap-1.5 text-[10px] font-medium text-[#6FA3D8]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#6FA3D8] animate-pulse" />
                Register open
              </span>
            </div>

            <div className="flex items-baseline gap-2">
              <p
                className="text-[2.15rem] font-medium text-[#8FBBE0] leading-none tabular-nums"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {hh}:{mm}:{ss}
              </p>
              <span
                className="text-[12px] font-medium text-[#6B7178] pb-0.5"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {period}
              </span>
            </div>
            <p className="text-[12px] text-[#6B7178] mt-2">{date}</p>

            {/* Shift progress — animates smoothly as the second ticks over */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span
                  className="text-[9.5px] uppercase tracking-[0.14em] text-[#6B7178] font-medium"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  Shift progress
                </span>
                <span className="text-[9.5px] text-[#6FA3D8] font-medium tabular-nums">
                  {Math.round(shiftPercent)}%
                </span>
              </div>
              <div className="h-1 w-full rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#6FA3D8]"
                  style={{
                    width: `${shiftPercent}%`,
                    transition: "width 700ms ease-out",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 flex items-center gap-1.5 text-[11px] text-[#6B7178]">
          <ShieldCheck size={13} />
          <span>Access restricted to registered supervisors</span>
        </div>
      </div>

      {/* ─── Right: sign-in card ─── */}
      <div className="w-full lg:w-[48%] flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[400px]">
          {/* Mobile header (small screens only) */}
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div
              className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
              style={{ background: "#2C4A6E", fontFamily: "'IBM Plex Mono', monospace" }}
            >
              S
            </div>
            <span
              className="text-[15px] font-medium text-[#1E1B16]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Sadguru Seva Sangh Trust
            </span>
          </div>

          <div className="bg-white rounded-2xl border border-[#E8E4DA] p-8 sm:p-10 shadow-[0_16px_40px_-20px_rgba(30,27,22,0.25)]">
            {children}
          </div>

          <p className="text-center text-[12px] text-[#85806F] mt-6">
            Trouble signing in? Contact your site administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;